import { getLucideIconName, getMarkerColor } from '@/lib/pin-categories';
import * as LucideIcons from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { ensureGoogleMaps } from './init';

interface MiniMapProps {
  place: {
    lat: number;
    lng: number;
    name: string;
  };
  pinStyle: string;
  apiKey: string;
  height?: string;
  onClick?: () => void;
}

export const MiniMap: React.FC<MiniMapProps> = ({ place, pinStyle, apiKey, height = '300px', onClick }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!mapRef.current || !apiKey) return;

      try {
        setLoading(true);
        setError(null);

        // Load Google Maps API (singleton - loads once per app)
        const google = await ensureGoogleMaps(apiKey);
        if (cancelled || !mapRef.current) return;

        // Import the maps and marker libraries (type-safe)
        const { Map } = (await google.maps.importLibrary('maps')) as google.maps.MapsLibrary;
        const { AdvancedMarkerElement, PinElement } = (await google.maps.importLibrary(
          'marker'
        )) as google.maps.MarkerLibrary;

        // Create the map centered on the place
        const map = new Map(mapRef.current, {
          center: { lat: place.lat, lng: place.lng },
          zoom: 15,
          mapId: 'GUIDEBOOK_MINI_MAP',
          disableDefaultUI: false,
          zoomControl: true,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
        });

        // Get pin styling
        const markerColor = getMarkerColor(pinStyle);
        const lucideIconName = getLucideIconName(pinStyle);

        // Render Lucide icon as SVG element
        const IconComponent = (LucideIcons as any)[lucideIconName] as React.ComponentType<any>;
        const iconSvgString = renderToStaticMarkup(<IconComponent size={20} strokeWidth={2.5} color="#ffffff" />);

        // Create a container for the SVG
        const iconContainer = document.createElement('div');
        iconContainer.innerHTML = iconSvgString;
        iconContainer.style.display = 'flex';
        iconContainer.style.alignItems = 'center';
        iconContainer.style.justifyContent = 'center';
        const svgElement = iconContainer.firstElementChild as HTMLElement;

        // Create custom pin with color and Lucide icon
        const pinElement = new PinElement({
          background: markerColor,
          borderColor: '#ffffff',
          glyph: svgElement,
          scale: 1.3,
        });

        // Add a marker at the place location
        new AdvancedMarkerElement({
          map: map,
          position: { lat: place.lat, lng: place.lng },
          title: place.name,
          content: pinElement.element,
        });

        setLoading(false);
      } catch (err: any) {
        if (!cancelled) {
          console.error('Error loading map:', err);
          setError('Failed to load map. Please try again later.');
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [place.lat, place.lng, place.name, pinStyle, apiKey]);

  return (
    <div
      onClick={onClick}
      style={{
        position: 'relative',
        width: '100%',
        height,
        borderRadius: '8px',
        overflow: 'hidden',
        border: '1px solid #e5e7eb',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      {loading && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#f9fafb',
            zIndex: 10,
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p style={{ marginTop: '8px', fontSize: '14px', color: '#6b7280' }}>Loading map...</p>
          </div>
        </div>
      )}

      {error && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#fef2f2',
            padding: '20px',
            textAlign: 'center',
          }}
        >
          <div>
            <p style={{ color: '#dc2626', fontWeight: 500 }}>{error}</p>
            <p style={{ marginTop: '8px', fontSize: '14px', color: '#6b7280' }}>{place.name}</p>
          </div>
        </div>
      )}

      <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
    </div>
  );
};
